/**
 * IFS user details slice. Stores the logged-in IFS user (WebUser, Identity, Description, etc.)
 * from api/get-ifs-user. Persists to localStorage when set; cleared on logout.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getIfsUserDetails } from "../../api/authAPI";
import { logger } from "../../../utils/logger";

const IFS_USER_STORAGE_KEY = "ifsUserDetails";

function readStored() {
  try {
    const raw = localStorage.getItem(IFS_USER_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeStored(user) {
  try {
    if (user) {
      localStorage.setItem(IFS_USER_STORAGE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(IFS_USER_STORAGE_KEY);
    }
  } catch (e) {
    logger.warn("ifsUserSlice: failed to persist to localStorage", e, "REDUX");
  }
}

export const fetchIfsUserDetails = createAsyncThunk(
  "ifsUser/fetchIfsUserDetails",
  async (_, { rejectWithValue }) => {
    try {
      const { data, status } = await getIfsUserDetails();
      if (status === 200 && data) {
        return data;
      }
      if (status === 204) {
        return null;
      }
      const msg = typeof data === "string" ? data : "Failed to load IFS user details";
      return rejectWithValue(msg);
    } catch (err) {
      const message = err?.response?.data != null
        ? (typeof err.response.data === "string" ? err.response.data : err.message)
        : err?.message ?? "Failed to load IFS user details";
      logger.error("fetchIfsUserDetails failed", err, "REDUX");
      return rejectWithValue(message);
    }
  }
);

const initialState = {
  user: readStored(),
  status: "idle",
  error: null,
};

const ifsUserSlice = createSlice({
  name: "ifsUser",
  initialState,
  reducers: {
    clearIfsUser(state) {
      state.user = null;
      state.status = "idle";
      state.error = null;
      writeStored(null);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIfsUserDetails.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchIfsUserDetails.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.user = action.payload;
        state.error = null;
        writeStored(action.payload);
      })
      .addCase(fetchIfsUserDetails.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload ?? "Unknown error";
      });
  },
});

export const { clearIfsUser } = ifsUserSlice.actions;
export default ifsUserSlice.reducer;
